import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Compass, FileText, Globe } from "lucide-react";
import Marquee from "@/components/Marquee";
import { MaskedLine, Overline, Reveal } from "@/components/Reveal";
import VesselFrame from "@/components/VesselFrame";
import { CONTACT } from "@/components/ContactCards";

const IMG = {
  hero: "https://images.unsplash.com/photo-1585713181935-d5f622cc2415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGNvbnRhaW5lciUyMHZlc3NlbCUyMG1hcml0aW1lfGVufDB8fHx8MTc5MDQxMzM2Nnww&ixlib=rb-4.1.0&q=85",
  container: "https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBvY2VhbiUyMGNvbnRhaW5lciUyMHZlc3NlbCUyMG1hcml0aW1lfGVufDB8fHx8MTc5MDQxMzM2Nnww&ixlib=rb-4.1.0&q=85",
  demolition: "https://images.unsplash.com/photo-1772870222730-8a69ebd936d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxzaGlwJTIwZGVtb2xpdGlvbiUyMHJlY3ljbGluZyUyMHNoaXBicmVha2luZyUyMHlhcmR8ZW58MHx8fHwxNzkwNDEzMzY1fDA&ixlib=rb-4.1.0&q=85",
  dubai: "https://images.unsplash.com/photo-1462007895615-c8c073bebcd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBqdW1laXJhaCUyMGxha2UlMjB0b3dlcnMlMjBza3lzY3JhcGVyfGVufDB8fHx8MTc5MDQxMzM3NHww&ixlib=rb-4.1.0&q=85",
  ocean: "https://images.unsplash.com/photo-1484387436194-cf7cb70800ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxvY2VhbiUyMHdhdGVyJTIwZGVlcCUyMGJsdWUlMjB0ZXh0dXJlfGVufDB8fHx8MTc5MDQxMzM3NHww&ixlib=rb-4.1.0&q=85",
};

const STATS = [
  { value: "20+", label: "Years of Maritime Brokerage" },
  { value: "500+", label: "Vessels Assessed & Valued" },
  { value: "6", label: "Global Partner Desks" },
  { value: "24H", label: "Enquiry Response" },
];

const HERO_TICKER = [
  { k: "HQ", v: "Dubai · JLT" },
  { k: "DESKS", v: "S&P / Recycling" },
  { k: "STANDARD", v: "HKC Compliant" },
  { k: "RESPONSE", v: "Within 24h" },
];

const TRUST = [
  { icon: ShieldCheck, title: "Discretion by Default", copy: "Off-market circulation, controlled data rooms and principal-to-principal negotiation. Tonnage changes hands quietly — we keep it that way." },
  { icon: Compass, title: "Valuation Discipline", copy: "Every candidate vessel is benchmarked against live demolition indices, charter earnings and recent comparable sales before a number is tabled." },
  { icon: FileText, title: "HKC-Compliant Recycling", copy: "Demolition placements follow the Hong Kong Convention with IHM documentation, vetted yards and full recycling audit trails." },
  { icon: Globe, title: "Global Reach, Local Presence", copy: "Headquartered in Dubai's Jumeirah Lake Towers with partner desks across Singapore, London, Athens, Alang and Chattogram." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    document.title = "Dice International LLC — Ship Sale & Purchase and Demolition Recycling Brokerage, Dubai";
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[calc(100vh-76px)] items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
          <img src={IMG.hero} alt="Ocean-going cargo vessel at sea" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#030F26] via-[#030F26]/55 to-[#030F26]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#030F26]/80 via-transparent to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-28 sm:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono text-xs font-medium uppercase tracking-[0.35em] text-[#C5A059]"
            data-testid="hero-overline"
          >
            Dice International LLC — Dubai, U.A.E
          </motion.p>
          <h1 className="mt-8 font-serif text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-heading">
            <MaskedLine delay={0.25}>Ship Sale &amp; Purchase.</MaskedLine>
            <MaskedLine delay={0.4}>Demolition &amp; Recycling.</MaskedLine>
            <MaskedLine delay={0.55}>
              <em className="text-[#C5A059]">Brokered with discretion.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheading"
          >
            A Dubai-based ship brokerage serving owners, cash buyers and recycling yards across global tonnage
            markets — from first valuation to final delivery.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-enquiry-button"
              className="group inline-flex items-center gap-3 bg-[#C5A059] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#030F26] shadow-md transition-colors duration-300 hover:bg-[#E2C08D]"
            >
              Contact the Desk
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/sale-and-purchase"
              data-testid="hero-services-button"
              className="inline-flex items-center gap-3 border border-[#C5A059]/50 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#C5A059] transition-colors duration-300 hover:border-[#C5A059] hover:bg-[#C5A059]/10"
            >
              Explore Services
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 1 }}
            className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 md:grid-cols-4"
            data-testid="hero-ticker"
          >
            {HERO_TICKER.map((item) => (
              <div key={item.k}>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-slate-500">{item.k}</p>
                <p className="mt-1.5 font-mono text-sm tracking-wide text-slate-200">{item.v}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="intro-section">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline testId="intro-overline">The House</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="intro-heading">
                A discreet desk for decisive tonnage deals.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                Dice International LLC brokers the two most consequential moments in a vessel's life: its sale
                into new ownership, and its final passage to responsible recycling. From our offices in Jumeirah
                Lake Towers, Dubai, we connect shipowners with qualified buyers, and end-of-life tonnage with
                vetted cash buyers and green recycling facilities.
              </p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
                Our practice is deliberately narrow and deep — Sale &amp; Purchase and Demolition &amp; Recycling —
                executed with the confidentiality, market intelligence and documentation rigour that serious
                principals expect.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10">
                <Link
                  to="/about"
                  data-testid="intro-about-link"
                  className="group inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.18em] text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]"
                >
                  About the House
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-5" data-testid="stats-bento">
              {STATS.map((s, i) => (
                <Reveal key={s.label} delay={i * 0.08} className="h-full">
                  <div className="flex h-full flex-col justify-between border border-[#1E3A6E]/60 bg-[#091B3E]/70 p-6 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#C5A059]/60">
                    <p className="font-serif text-4xl font-semibold text-[#C5A059]">{s.value}</p>
                    <p className="mt-6 font-mono text-[0.65rem] uppercase leading-relaxed tracking-[0.2em] text-slate-400">
                      {s.label}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="border-t border-[#1E3A6E]/40 bg-[#071838]/30" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <Reveal>
            <Overline testId="services-overline">What We Broker</Overline>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
              <h2 className="max-w-xl font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="services-heading">
                Two disciplines. One standard of execution.
              </h2>
              <Link
                to="/contact"
                data-testid="services-enquiry-link"
                className="group inline-flex items-center gap-2 text-sm font-medium uppercase tracking-[0.18em] text-[#C5A059] transition-colors duration-300 hover:text-[#E2C08D]"
              >
                Contact the Desk
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-12">
            <Reveal className="md:col-span-7">
              <Link to="/sale-and-purchase" data-testid="service-card-sp" className="group block h-full">
                <VesselFrame src={IMG.container} alt="Container vessel under cargo operations" testId="service-sp-frame" className="h-80 sm:h-96">
                </VesselFrame>
                <div className="relative -mt-24 ml-6 mr-6 border border-[#1E3A6E]/60 bg-[#030F26]/90 p-7 backdrop-blur-xl transition-colors duration-300 group-hover:border-[#C5A059]/60 sm:ml-10">
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059]">01 — Core Desk</p>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-white sm:text-2xl">Sale &amp; Purchase</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    Secondhand tonnage across dry bulk, tanker, container and specialised segments — valuation,
                    confidential marketing, MOA negotiation and closing.
                  </p>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-5">
              <Link to="/demolition-recycling" data-testid="service-card-demolition" className="group block h-full">
                <VesselFrame src={IMG.demolition} alt="Vessel at a ship recycling yard" testId="service-demolition-frame" className="h-80 sm:h-96" />
                <div className="relative -mt-24 ml-6 mr-6 border border-[#1E3A6E]/60 bg-[#030F26]/90 p-7 backdrop-blur-xl transition-colors duration-300 group-hover:border-[#C5A059]/60 sm:ml-10">
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059]">02 — Core Desk</p>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-white sm:text-2xl">Demolition &amp; Recycling</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">
                    End-of-life placement with vetted cash buyers and HKC-compliant green recycling yards — from
                    LDT valuation to title transfer.
                  </p>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32" data-testid="trust-section">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline testId="trust-overline">Why Principals Instruct Us</Overline>
              <h2 className="mt-6 font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="trust-heading">
                Trust is the true currency of brokerage.
              </h2>
              <p className="mt-8 text-base leading-relaxed text-slate-400">
                Shipowners and cash buyers return to a desk that protects their position, prices honestly and
                delivers clean documentation. That is the entire business model.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <VesselFrame src={IMG.ocean} alt="Deep blue ocean texture" testId="trust-ocean-frame" className="mt-10 h-72" />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div data-testid="trust-pillars">
              {TRUST.map((t, i) => (
                <Reveal key={t.title} delay={i * 0.08}>
                  <div className="group flex gap-6 border-t border-[#1E3A6E]/50 py-9 transition-colors duration-300 first:border-t-0 first:pt-0 sm:gap-10">
                    <span className="font-mono text-sm text-[#C5A059]">0{i + 1}</span>
                    <div>
                      <div className="flex items-center gap-4">
                        <t.icon className="h-5 w-5 text-[#C5A059]" strokeWidth={1.5} />
                        <h3 className="font-serif text-xl font-semibold text-white sm:text-2xl">{t.title}</h3>
                      </div>
                      <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">{t.copy}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DUBAI BAND + CTA */}
      <section className="relative overflow-hidden border-t border-[#1E3A6E]/40" data-testid="cta-section">
        <img src={IMG.dubai} alt="Dubai skyline at dusk" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#030F26]/82" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:py-32">
          <Reveal>
            <Overline testId="cta-overline">Jumeirah Bay Tower X2 · Cluster X · JLT</Overline>
            <h2 className="mt-6 max-w-3xl font-serif text-2xl font-medium leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="cta-heading">
              Discuss your vessel with a broker — <em className="text-[#C5A059]">in confidence.</em>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
              Whether you are acquiring tonnage, selling a unit, or placing a vessel for recycling, the desk
              responds within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="cta-enquiry-button"
                className="group inline-flex items-center gap-3 bg-[#C5A059] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#030F26] transition-colors duration-300 hover:bg-[#E2C08D]"
              >
                Contact the Desk
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-button"
                className="inline-flex items-center gap-3 border border-[#C5A059]/50 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-[#C5A059] transition-colors duration-300 hover:border-[#C5A059] hover:bg-[#C5A059]/10"
              >
                WhatsApp the Desk
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
